import {
	FRAuth,
	FRLoginFailure,
	FRLoginSuccess,
	FRStep,
	TokenManager,
	UserManager
} from "@forgerock/javascript-sdk"
import { LoginError } from "@models/error/login-error"
import { ForgeRockCallback } from "@models/forgeRock.model"
import { UserProfile } from "@models/userProfile.model"
import { AuthService } from "./authService"
import oauthService from "./oauthService"

export interface ForgeRockStep {
	type: string
	callbacks?: ForgeRockCallback[]
	payload?: {
		code?: string | number
		message?: string
		reason?: string
		[key: string]: unknown
	}
}

// Type definitions for ForgeRock SDK that may not be fully typed
interface ForgeRockSDKExtended {
	logout?: () => Promise<void>
}

interface TokenManagerExtended {
	clearTokens?: () => void
	hasTokens?: () => boolean
}

interface TokensOptions {
	query?: {
		code: string
	}
	forceRenew?: boolean
}

interface DecodedJWTToken {
	sub?: string
	subname?: string
	username?: string
	[key: string]: unknown
}

interface ForgeRockUserInfo {
	sub?: string
	preferred_username?: string
	given_name?: string
	family_name?: string
	email?: string
	locale?: string
}

interface ForgeRockStepInternal {
	type?: string
	callbacks?: ForgeRockCallback[]
	payload?: {
		code?: string | number
		message?: string
		reason?: string
		[key: string]: unknown
	}
	constructor: {
		name: string
	}
}

const transformStep = (
	step: FRStep | FRLoginSuccess | FRLoginFailure
): ForgeRockStep => {
	const stepInternal = step as unknown as ForgeRockStepInternal

	// Check if this is a LoginSuccess instance
	if (step instanceof FRLoginSuccess) {
		return {
			type: "LoginSuccess",
			callbacks: stepInternal.callbacks,
			payload: stepInternal.payload
		}
	}

	// Check if the step has tokenId, successUrl, or realm in payload (indicates successful auth)
	const payload = stepInternal.payload || {}
	if (payload.tokenId || payload.successUrl || payload.realm) {
		return {
			type: "LoginSuccess",
			callbacks: stepInternal.callbacks,
			payload: stepInternal.payload
		}
	}

	// Check if the step type indicates success
	const stepType = stepInternal.type || step.constructor.name
	if (stepType === "LoginSuccess" || stepType === "FRLoginSuccess") {
		return {
			type: "LoginSuccess",
			callbacks: stepInternal.callbacks,
			payload: stepInternal.payload
		}
	}

	return {
		type: stepType,
		callbacks: stepInternal.callbacks,
		payload: stepInternal.payload
	}
}

let startJourneyPromise: Promise<ForgeRockStep> | null = null

export const startJourney = async (): Promise<ForgeRockStep> => {
	if (startJourneyPromise) {
		return startJourneyPromise
	}

	startJourneyPromise = (async (): Promise<ForgeRockStep> => {
		try {
			try {
				const tokenManagerExtended = TokenManager as TokenManagerExtended
				if (tokenManagerExtended.clearTokens) {
					tokenManagerExtended.clearTokens()
				}
			} catch (cleanupError) {
				if (process.env.NODE_ENV === "development") {
					console.warn("Error cleaning up before journey start:", cleanupError)
				}
			}

			const initialStep = await FRAuth.start()
			return transformStep(initialStep)
		} catch (error) {
			if (process.env.NODE_ENV === "development") {
				console.error("Authentication error occurred", {
					errorType: error instanceof Error ? error.name : "Unknown",
					message: "Failed to start authentication journey",
					timestamp: new Date().toISOString()
				})
			}
			throw new LoginError(
				"Failed to start authentication journey",
				"JOURNEY_START_ERROR",
				0
			)
		} finally {
			startJourneyPromise = null
		}
	})()

	return startJourneyPromise
}

export const resetJourneyCache = (): void => {
	startJourneyPromise = null
}

export const submitStep = async (step: FRStep): Promise<ForgeRockStep> => {
	try {
		const nextStep = await FRAuth.next(step)
		return transformStep(nextStep)
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		// Only log error type and timestamp, never full error objects with sensitive data
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Authentication failed",
				timestamp: new Date().toISOString()
				// Never log: full error object, credentials, or authentication details
			})
		}
		throw new LoginError("Authentication failed", "AUTHENTICATION_ERROR", 0)
	}
}

export const getCurrentUser = async (): Promise<UserProfile | null> => {
	try {
		const userInfo = (await UserManager.getCurrentUser()) as ForgeRockUserInfo
		if (!userInfo) return null

		// Security: Transform and validate ForgeRock user info to UserProfile format
		// Never trust OAuth userInfo without validation - always sanitize
		const loginUserName = (
			userInfo.sub ||
			userInfo.preferred_username ||
			""
		).trim()
		const agentFirstName = (userInfo.given_name || "").trim()
		const agentLastName = (userInfo.family_name || "").trim()
		const emailAddress = (userInfo.email || "").trim()

		// Security: Critical fields must be validated - default values should not grant privileges (CWE-522)
		// All IDs default to 0 (invalid) until validated by backend
		// All permission flags default to false (no access) until validated by backend
		// Role codes default to empty (no role) until validated by backend
		return {
			loginUserName,
			agentFirstName,
			agentLastName,
			emailAddress,
			agentEmailAddress: emailAddress,
			// Security: These defaults provide NO privileges - backend must validate and populate
			agentId: 0, // Invalid ID - must be validated by backend
			allowedApplicationTools: [], // No tools granted by default
			headquarterGroupSailingsAvailable: false, // No privileges by default
			isHeadquarter: false, // Not headquarter by default
			dismissedNotifications: [], // Empty array
			agencyId: 0, // Invalid ID - must be validated by backend
			agencyName: "",
			urlAddress: "",
			homeBasedAgent: false,
			languageCode: (userInfo.locale || "en-US").trim(),
			localeCountryCode: "US",
			countryCode: "US",
			reportingOfficeCode: "",
			headQuarterAgencyId: null,
			registrationDate: new Date().toISOString(),
			colonialUserId: null,
			internationalRepFlag: false,
			roleCode: "", // Security: Empty role by default - backend must assign privileges
			agencyCategoryType: null,
			eQuoteAcceptanceTC: false,
			currencies: [],
			primaryCurrency: "USD",
			// Security: All access flags default to false - no privileges granted
			hasFlexPayAccess: false,
			hasEquoteAccess: false,
			agentPhotos: {
				AGENT_HOMEPAGE_PHOTO: {
					uri: "",
					defaultFlag: true,
					pixelHeight: 0,
					pixelWidth: 0,
					fullPath: ""
				}
			},
			locationData: [],
			latteAvailable: false,
			lat: "",
			long: "",
			agencyPhone: "",
			agentCountryCode: "US",
			reAcceptTerms: false
		} as UserProfile
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		// Only log error type, never full error objects with user data
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Error getting current user",
				timestamp: new Date().toISOString()
				// Never log: full error object, user info, or authentication details
			})
		}
		return null
	}
}

export const logout = async (): Promise<void> => {
	try {
		// Try to use the ForgeRock SDK logout if available
		const frAuthExtended = FRAuth as ForgeRockSDKExtended
		if (frAuthExtended.logout) {
			await frAuthExtended.logout()
		}

		// Clear tokens from the SDK
		const tokenManagerExtended = TokenManager as TokenManagerExtended
		if (tokenManagerExtended.clearTokens) {
			tokenManagerExtended.clearTokens()
		}
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		// Only log error type, never full error objects
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Error during logout",
				timestamp: new Date().toISOString()
				// Never log: full error object or authentication details
			})
		}
		// Ensure tokens are cleared even if something fails
		const tokenManagerExtended = TokenManager as TokenManagerExtended
		if (tokenManagerExtended.clearTokens) {
			tokenManagerExtended.clearTokens()
		}
	}
}

export const getAccessToken = async (
	authCode: string
): Promise<string | null> => {
	try {
		const tokens = await TokenManager.getTokens({
			query: {
				code: authCode
			}
		} as TokensOptions)
		return tokens?.accessToken || null
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		// Never log tokens, auth codes, or full error objects
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Error getting access token",
				timestamp: new Date().toISOString()
				// Never log: tokens, auth codes, or full error object
			})
		}
		return null
	}
}

export const getIdToken = async (authCode: string): Promise<string | null> => {
	try {
		const tokens = await TokenManager.getTokens({
			query: {
				code: authCode
			}
		} as TokensOptions)
		return tokens?.idToken || null
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		// Never log tokens, auth codes, or full error objects
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Error getting ID token",
				timestamp: new Date().toISOString()
				// Never log: tokens, auth codes, or full error object
			})
		}
		return null
	}
}

export const getRefreshToken = async (
	authCode: string
): Promise<string | null> => {
	try {
		const tokens = await TokenManager.getTokens({
			query: {
				code: authCode
			}
		} as TokensOptions)
		return tokens?.refreshToken || null
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		// Never log tokens, auth codes, or full error objects
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Error getting refresh token",
				timestamp: new Date().toISOString()
				// Never log: tokens, auth codes, or full error object
			})
		}
		return null
	}
}

export const getUsername = async (token: string): Promise<string | null> => {
	const decoded = AuthService.decodeJWTToken(token) as DecodedJWTToken | null
	if (!decoded || typeof decoded !== "object") return null
	return typeof decoded.subname === "string"
		? decoded.subname
		: typeof decoded.sub === "string"
			? decoded.sub
			: typeof decoded.username === "string"
				? decoded.username
				: null
}

export const isAuthenticated = (): boolean => {
	try {
		const tokenManagerExtended = TokenManager as TokenManagerExtended
		return tokenManagerExtended.hasTokens?.() || false
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Error checking authentication status",
				timestamp: new Date().toISOString()
				// Never log: full error object or authentication details
			})
		}
		return false
	}
}

export const refreshTokens = async (): Promise<{
	accessToken: string
	idToken?: string
} | null> => {
	try {
		// Silent, fully-background renewal via the OAuth refresh_token grant.
		// The refresh token is the credential, so no SSO cookie / iframe /
		// redirect is needed — the user notices nothing. Requires the login
		// scope to include `offline_access` so a refresh token was issued; if
		// none is stored we return null and the caller can fall back to login.
		const refreshToken = AuthService.getRefreshToken()
		if (!refreshToken) return null

		const tokens = await oauthService.refreshAccessToken(refreshToken)
		if (!tokens?.access_token) return null

		// Persist the new tokens (AM may rotate the refresh token; if it doesn't
		// return one, keep the existing one).
		AuthService.setAuthToken(
			tokens.access_token,
			tokens.id_token,
			tokens.refresh_token ?? refreshToken
		)

		return { accessToken: tokens.access_token, idToken: tokens.id_token }
	} catch (error) {
		// Security: Never log sensitive authentication details (CWE-532)
		// Never log tokens or full error objects
		if (process.env.NODE_ENV === "development") {
			console.error("Authentication error occurred", {
				errorType: error instanceof Error ? error.name : "Unknown",
				message: "Error refreshing tokens",
				timestamp: new Date().toISOString()
				// Never log: tokens or full error object
			})
		}
		return null
	}
}

// Legacy namespace export for backward compatibility
export const ForgeRockAuthService = {
	startJourney,
	submitStep,
	getCurrentUser,
	logout,
	getAccessToken,
	getIdToken,
	getRefreshToken,
	getUsername,
	isAuthenticated,
	refreshTokens,
	resetJourneyCache
} as const
